import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Input, Spinner, Table } from 'reactstrap';
import productService from '../../services/productService';
import { formatPrice } from '../../helpers/utility';
import MovementDocumentModal from '../Stock/MovementDocumentModal';
import ProductPriceChart from './ProductPriceChart';

const statuses = {
  RECEPTION: { EN_ATTENTE: 'En attente', VALIDE: 'Validée', REFUSE: 'Refusée' },
  DELIVERY: { EN_PREPARATION: 'En préparation', EN_COURS: 'En cours', LIVRE: 'Livré', ANNULE: 'Annulé' },
};
const quantity = value => value == null ? '—' : Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 3 });
const price = value => value == null ? 'Non renseigné' : formatPrice(value);
const toHT = (value, vat) => {
  if (value == null || value === '') return null;
  const amount = Number(value), rate = Number(vat ?? 0);
  return Number.isFinite(amount) && Number.isFinite(rate) && rate >= 0 ? amount / (1 + rate) : null;
};
const date = value => value ? value.split('-').reverse().join('/') : '—';

export default function ProductDocumentLines({ product, onClose }) {
  const [type, setType] = useState('RECEPTION');
  const [sort, setSort] = useState('date');
  const [direction, setDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const [document, setDocument] = useState(null);
  const receipt = type === 'RECEPTION';
  const purchasePrice = toHT(product.prixAchatTTC, product.tauxTVAAchat);
  const salePrice = product.prixVariable ? null : toHT(product.prixVenteTTC1, product.tauxTVAVente);
  const margin = Number.isFinite(purchasePrice) && Number.isFinite(salePrice) ? salePrice - purchasePrice : null;
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ loading: true, data: null, error: '' });
    const params = { type, sort, direction, page, size: 25, ht: true };
    productService.documentLines(product.id, params, controller.signal).then(data => {
      if (!Array.isArray(data?.content)) throw new Error('Réponse invalide');
      if (active) setState({ loading: false, data, error: '' });
    }).catch(() => {
      if (active) setState({ loading: false, data: null, error: 'Impossible de charger les lignes. Réessayez.' });
    });
    return () => { active = false; controller.abort(); };
  }, [product.id, type, sort, direction, page, refresh]);

  const changeType = next => { setType(next); setPage(0); };
  const sortBy = key => {
    setDirection(sort === key && direction === 'desc' ? 'asc' : 'desc'); setSort(key); setPage(0);
  };
  const heading = (key, label) => <th scope="col" aria-sort={sort === key ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
    <button type="button" className="product-lines-sort" onClick={() => sortBy(key)}>{label} {sort === key ? direction === 'asc' ? '↑' : '↓' : '↕'}</button>
  </th>;

  if (document) return <main className="product-lines-page"><MovementDocumentModal inline movement={document} onClose={() => setDocument(null)} /></main>;
  return <main className="product-lines-page">
    <header className="product-lines-page-header"><div><h1>Réceptions / BL · {product.libelle}</h1>
      <small className="d-block text-muted">{product.codeBarre || product.code} · Prix d'achat fiche HT : {price(purchasePrice)}</small>
    </div><Button color="light" className="border" onClick={onClose}>← Retour aux articles</Button></header>
    <section className="product-current-margin" aria-label="Marge brute actuelle HT">
      <div><span>Achat HT actuel</span><strong>{price(purchasePrice)}</strong></div>
      <div><span>Vente HT · tarif 1</span><strong>{product.prixVariable ? 'Prix variable' : price(salePrice)}</strong></div>
      <div className={margin < 0 ? 'product-margin-negative' : 'product-margin-value'}><span>Marge brute HT / unité</span>
        <strong>{margin == null ? 'Indisponible' : formatPrice(margin)}</strong></div>
      <small>Prix actuels de la fiche · marge unitaire = vente HT − achat HT</small>
    </section>
    <section className="product-lines-page-content">
      <div className="product-lines-toolbar">
        <div role="group" aria-label="Type de document" className="btn-group">
          <Button color={receipt ? 'primary' : 'light'} aria-pressed={receipt} onClick={() => changeType('RECEPTION')}>Réceptions</Button>
          <Button color={!receipt ? 'primary' : 'light'} aria-pressed={!receipt} onClick={() => changeType('DELIVERY')}>Bons de livraison / tickets</Button>
        </div>
        <Button color="light" disabled={state.loading} onClick={() => setRefresh(value => value + 1)}>Actualiser</Button>
      </div>
      <ProductPriceChart articleId={product.id} type={type} refresh={refresh} />
      <p className="product-lines-note">Quantités {receipt ? 'reçues, gratuites incluses' : 'du BL, avec quantité livrée distincte'} · montants avant remise globale · prix fiche signalé si le coût manque.</p>
      {state.loading ? <div className="text-center py-5" role="status"><Spinner size="sm" /> Chargement des lignes…</div>
        : state.error ? <Alert color="danger">{state.error} <Button color="link" onClick={() => setRefresh(value => value + 1)}>Réessayer</Button></Alert>
          : <>
            <div className="product-lines-results"><span>{state.data.totalElements} ligne(s)</span>
              <label>Trier par <Input type="select" value={sort} onChange={event => { setSort(event.target.value); setPage(0); }}>
                <option value="date">Date</option><option value="purchasePrice">Prix d'achat HT</option><option value="quantity">Quantité</option>
              </Input></label><Button color="light" onClick={() => { setDirection(direction === 'asc' ? 'desc' : 'asc'); setPage(0); }}>{direction === 'asc' ? 'Croissant ↑' : 'Décroissant ↓'}</Button>
            </div>
            <Table responsive hover size="sm" className="product-lines-table align-middle">
              <thead><tr>{heading('date', 'Date')}<th>Document</th><th>{receipt ? 'Fournisseur' : 'Client'} / entrepôt</th><th>Statut</th>
                {heading('quantity', receipt ? 'Qté reçue' : 'Quantité')}<th>{receipt ? 'Gratuites' : 'Livrée'}</th>
                {heading('purchasePrice', 'Achat unit. HT')}{!receipt && <th>Vente unit. HT</th>}<th>Montant ligne HT</th>
              </tr></thead><tbody>{state.data.content.map(line => <tr key={line.lineId}>
                <td className="text-nowrap">{date(line.date)}</td><td><Button color="link" className="p-0" onClick={() => setDocument({
                  sourceDocumentType: receipt ? 'PURCHASE_RECEIPT' : 'CUSTOMER_DELIVERY', sourceDocumentId: line.documentId, referenceDocument: line.reference,
                })}>{line.reference}</Button><small className="d-block text-muted">Ligne #{line.lineId}</small></td>
                <td>{line.party}<small className="d-block text-muted">{line.warehouse || 'Sans entrepôt'}</small></td>
                <td><Badge color="light" className="text-dark">{statuses[type][line.status] || line.status || '—'}</Badge></td>
                <td className="text-end">{quantity(line.quantity)}</td><td className="text-end">{quantity(receipt ? line.freeQuantity : line.deliveredQuantity)}</td>
                <td className="text-end">{price(line.purchasePrice)}{line.articlePriceFallback && <small className="d-block text-muted">Prix fiche article</small>}</td>
                {!receipt && <td className="text-end">{price(line.unitPrice)}</td>}<td className="text-end">{price(line.amount)}</td>
              </tr>)}{!state.data.content.length && <tr><td colSpan={receipt ? 8 : 9} className="text-center py-4">Aucune ligne pour ces critères.</td></tr>}</tbody>
            </Table>
            <div className="product-lines-pagination"><Button color="light" disabled={page === 0} onClick={() => setPage(value => value - 1)}>Précédent</Button>
              <span>Page {page + 1} / {Math.max(1, state.data.totalPages)}</span><Button color="light" disabled={page + 1 >= state.data.totalPages} onClick={() => setPage(value => value + 1)}>Suivant</Button></div>
          </>}
    </section>
  </main>;
}
